import {
  AddressType,
  ComplianceCaseTypeEnum,
  ComplianceExternalObjectType,
  IndividualAccountTypeEnum,
  InstitutionAccountTypeEnum,
  JointAccountTypeEnum,
  TaxIdType,
} from "~/graphql/supergraphEnums";
import { extractIdFromPathSegment } from "~/pages/Compliance/utils";
import { MAIN_SERVICE, ObjectTypeEnum } from "~/pages/Compliance/utils/constants";
import { toDbId } from "~/utils/graphql-helpers";
import type { WizardCaseInfo, WizardProfileData } from "../types";
import type {
  Address,
  ComplianceIndividualProfileInput,
  ComplianceInstitutionProfileInput,
  ComplianceJointProfileInput,
  ExternalObjectInput,
} from "~/graphql/supergraphTypes";

export type ExternalObjects = {
  externalArena: ExternalObjectInput;
  externalEntity: ExternalObjectInput;
  caseExternalObject?: ExternalObjectInput;
};

export const buildExternalObjects = (caseInfo: WizardCaseInfo): ExternalObjects => {
  const externalArena: ExternalObjectInput = {
    objectId: toDbId(caseInfo.clientId ?? "").toString(),
    objectTypeEnum: ObjectTypeEnum.Arena,
    service: MAIN_SERVICE,
    name: caseInfo.clientName ?? "",
  };

  const externalEntity: ExternalObjectInput = {
    objectId: toDbId(caseInfo.entityId ?? "").toString(),
    objectTypeEnum: ObjectTypeEnum.Account,
    service: MAIN_SERVICE,
    name: caseInfo.entityName ?? "",
  };

  return {
    externalArena,
    externalEntity,
    caseExternalObject: buildCaseExternalObject(caseInfo),
  };
};

export const resolveCaseType = (
  caseExternalObject?: ExternalObjectInput,
): ComplianceCaseTypeEnum =>
  caseExternalObject?.objectTypeEnum === ComplianceExternalObjectType.Position
    ? ComplianceCaseTypeEnum.Remediation
    : ComplianceCaseTypeEnum.InvestorOnboarding;

const buildCaseExternalObject = (caseInfo: WizardCaseInfo): ExternalObjectInput | undefined => {
  switch (caseInfo.creationMethod) {
    case "position":
      return buildPositionObject(caseInfo);
    case "prospect":
      return buildProspectObject(caseInfo);
    case "client":
      return undefined;
  }
};

const buildPositionObject = (caseInfo: WizardCaseInfo): ExternalObjectInput | undefined => {
  if (!caseInfo.positionId) {
    return undefined;
  }
  const positionId = extractIdFromPathSegment(caseInfo.positionId, "position");
  return positionId
    ? {
        objectId: positionId.toString(),
        objectTypeEnum: ComplianceExternalObjectType.Position,
        service: MAIN_SERVICE,
      }
    : undefined;
};

const buildProspectObject = (caseInfo: WizardCaseInfo): ExternalObjectInput | undefined => {
  if (!caseInfo.prospectIdOrSubscriptionUrl) {
    return undefined;
  }
  const prospectId = extractIdFromPathSegment(caseInfo.prospectIdOrSubscriptionUrl, "prospect");
  return prospectId
    ? {
        objectId: prospectId.toString(),
        objectTypeEnum: ComplianceExternalObjectType.Prospect,
        service: MAIN_SERVICE,
      }
    : undefined;
};

const buildAddresses = (profile: WizardProfileData): Address[] => {
  const address = profile.address;
  return [
    {
      addressType: AddressType.Unknown,
      street: address?.streetLine1,
      street2: address?.streetLine2,
      city: address?.city,
      state: address?.stateOrProvince,
      postalCode: address?.zipCode,
      country: address?.country || "US",
    },
  ];
};

export const buildIndividualInput = (
  profile: WizardProfileData,
): ComplianceIndividualProfileInput => ({
  // category gates which builder runs, so accountType is always a valid IndividualAccountTypeEnum here.
  accountType: profile.accountType
    ? (profile.accountType as unknown as IndividualAccountTypeEnum)
    : undefined,
  firstName: profile.firstName ?? "",
  middleName: profile.middleName,
  lastName: profile.lastName ?? "",
  dateOfBirth: profile.dateOfBirth,
  taxId: profile.taxId,
  taxIdType: profile.taxIdType,
  addresses: buildAddresses(profile),
});

export const buildInstitutionInput = (
  profile: WizardProfileData,
): ComplianceInstitutionProfileInput => ({
  // category gates which builder runs, so accountType is always a valid InstitutionAccountTypeEnum here.
  accountType: profile.accountType
    ? (profile.accountType as unknown as InstitutionAccountTypeEnum)
    : undefined,
  legalBusinessName: profile.legalBusinessName ?? "",
  dateOfFormation: profile.dateOfFormation,
  taxId: profile.taxId,
  // The entity form does not collect a tax-id type; institutions are assumed to use an EIN.
  taxIdType: profile.taxIdType ?? TaxIdType.Ein,
  addresses: buildAddresses(profile),
  amlEntityId: profile.amlEntityId,
});

export const buildJointInput = (profile: WizardProfileData): ComplianceJointProfileInput => ({
  // category gates which builder runs, so accountType is always a valid JointAccountTypeEnum here.
  accountType: profile.accountType
    ? (profile.accountType as unknown as JointAccountTypeEnum)
    : undefined,
  legalBusinessName: profile.legalBusinessName ?? "",
  addresses: profile.domicileCountry
    ? [{ addressType: "primary_domicile", domicile: profile.domicileCountry }]
    : undefined,
});
